import { forwardRef } from 'react';
import type { CSSProperties, HTMLAttributes } from 'react';
import { kaiTypographyWeb, pxToRem } from '../../typography';
import { adminCommonColors } from './adminCommonColors';
import { resolveStyleOverride, type StyleOverride } from './styleOverrides';

export type SortDropdownOption = {
  value: string;
  label: string;
};

export type SortDropdownProps = Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> & {
  value: string;
  options: SortDropdownOption[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  menuLabel?: string;
  fallbackLabel?: string;
  styleOverride?: StyleOverride;
};

const neutral = {
  50: adminCommonColors.surfaceMuted,
  300: adminCommonColors.border,
  500: adminCommonColors.textSecondary,
  900: adminCommonColors.text,
};

const SORT_DROPDOWN_ROOT_STYLE: CSSProperties = {
  position: 'relative',
  width: pxToRem(187),
};

const SORT_BUTTON_STYLE: CSSProperties = {
  width: '100%',
  height: pxToRem(40),
  minHeight: '40px', // floor so the icon/chevron slots never get squeezed below their own size
  padding: '0 10px',
  border: 0,
  borderRadius: 5,
  background: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  cursor: 'pointer',
  color: neutral[900],
  overflow: 'visible', // was 'clip' - clipped the icon/text once they grew past the old fixed 40px height
};

const SORT_BUTTON_LEFT_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
};

const ICON_SLOT_STYLE: CSSProperties = {
  width: pxToRem(24),
  height: pxToRem(24),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const SORT_BUTTON_LABEL_STYLE: CSSProperties = {
  ...kaiTypographyWeb.xSmallBold,
  color: neutral[900],
  lineHeight: 1.167, // matches xSmallBold's font-size (14px / 12px)
  letterSpacing: '0.24px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const SORT_BUTTON_CHEVRON_STYLE: CSSProperties = {
  ...ICON_SLOT_STYLE,
  transition: 'transform 160ms ease',
};

const SORT_BUTTON_CHEVRON_OPEN_STYLE: CSSProperties = {
  transform: 'rotate(180deg)',
};

const SORT_PANEL_STYLE: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 5px)',
  right: 0,
  width: '100%',
  borderRadius: 5,
  background: '#FFFFFF',
  border: `1px solid ${neutral[300]}`,
  boxShadow: '0 4px 3px rgba(29, 29, 29, 0.1), 0 10px 7.5px rgba(29, 29, 29, 0.1)',
  padding: '12px 0 0',
  overflow: 'hidden',
  zIndex: 20,
};

const SORT_PANEL_TITLE_STYLE: CSSProperties = {
  ...kaiTypographyWeb.xSmallBold,
  color: neutral[500],
  textTransform: 'uppercase',
  letterSpacing: '0.24px',
  lineHeight: 1.167, // matches xSmallBold's font-size (14px / 12px)
  padding: '0 20px 11px',
};

const SORT_OPTIONS_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  borderTop: `1px solid ${neutral[300]}`,
  padding: 0,
};

const SORT_OPTION_STYLE: CSSProperties = {
  width: '100%',
  border: 0,
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '5px 10px 5px 20px',
  cursor: 'pointer',
  textAlign: 'left',
  color: neutral[900],
  borderRadius: 5,
};

const SORT_OPTION_ACTIVE_STYLE: CSSProperties = {
  background: neutral[50],
};

const SORT_OPTION_LABEL_STYLE: CSSProperties = {
  ...kaiTypographyWeb.small,
  color: neutral[900],
  lineHeight: 1.143, // matches small's font-size (16px / 14px)
  letterSpacing: '0.28px',
};

const SORT_OPTION_LABEL_ACTIVE_STYLE: CSSProperties = {
  ...kaiTypographyWeb.smallBold,
  color: neutral[900],
  lineHeight: 1.143, // matches smallBold's font-size (16px / 14px)
  letterSpacing: '0.28px',
};

const SortIcon = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' fill='none' aria-hidden>
    <path d='M2 4H16' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
    <path d='M5 9H13' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
    <path d='M8 14H10' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden>
    <path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const CheckIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' aria-hidden>
    <path d='M5 12.5L9.5 17L19 7.5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

export const SortDropdown = forwardRef<HTMLDivElement, SortDropdownProps>(
  (
    {
      value,
      options,
      isOpen,
      onToggle,
      onSelect,
      menuLabel = 'Sort By',
      fallbackLabel,
      styleOverride: rootCss,
      style,
      ...props
    },
    ref,
  ) => {
    const selectedLabel = options.find(option => option.value === value)?.label ?? fallbackLabel ?? options[0]?.label ?? 'Sort';

    return (
      <div
        ref={ref}
        style={{
          ...SORT_DROPDOWN_ROOT_STYLE,
          ...style,
          ...resolveStyleOverride(rootCss),
        }}
        {...props}
      >
        <button
          type='button'
          style={SORT_BUTTON_STYLE}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-haspopup='menu'
        >
          <span style={SORT_BUTTON_LEFT_STYLE}>
            <span style={ICON_SLOT_STYLE}>
              <SortIcon />
            </span>
            <span style={SORT_BUTTON_LABEL_STYLE}>{selectedLabel}</span>
          </span>
          <span
            style={{
              ...SORT_BUTTON_CHEVRON_STYLE,
              ...(isOpen ? SORT_BUTTON_CHEVRON_OPEN_STYLE : undefined),
            }}
          >
            <ChevronDownIcon />
          </span>
        </button>
        {isOpen && (
          <div style={SORT_PANEL_STYLE}>
            <div style={SORT_PANEL_TITLE_STYLE}>{menuLabel.toUpperCase()}</div>
            <div role='menu' aria-label={menuLabel} style={SORT_OPTIONS_STYLE}>
              {options.map(option => {
                const isSelected = value === option.value;

                return (
                  <button
                    key={option.value}
                    type='button'
                    role='menuitemradio'
                    aria-checked={isSelected}
                    style={{
                      ...SORT_OPTION_STYLE,
                      ...(isSelected ? SORT_OPTION_ACTIVE_STYLE : undefined),
                    }}
                    onClick={() => onSelect(option.value)}
                  >
                    <span
                      style={isSelected ? SORT_OPTION_LABEL_ACTIVE_STYLE : SORT_OPTION_LABEL_STYLE}
                    >
                      {option.label}
                    </span>
                    {isSelected && (
                      <span style={ICON_SLOT_STYLE}>
                        <CheckIcon />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  },
);

SortDropdown.displayName = 'SortDropdown';
